///
/// EarthBrowser
///
/// Created by Matt Giger on 4/14/2026
/// Copyright (c) 2026 Geodata Labs
///

import { AnchoredPanelShell, type PanelShellContext } from "@/components/controls/anchored-panel/AnchoredPanelShell"
import { PanelActionTooltip } from "@/components/controls/anchored-panel/PanelHeaderActions"
import {
	Badge,
	CoordFooter,
	CurrentConditionsSection,
	formatTimeAgo,
	InfoField,
	MoreSection
} from "@/components/controls/anchored-panel/panelAtoms"
import type { EarthquakeProperties } from "@/earthbrowser/dataset-distribution/earthquakes/EarthquakesTable"
import { formatDepth } from "@/earthbrowser/utils/unitFormatters"
import { setQuakeCutout } from "@/redux/dialogSlice"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { cn } from "@/utils/utils"
import { Cuboid } from "lucide-react"

export function AnchoredEarthquakeInfoPanel() {
	return (
		<AnchoredPanelShell
			match={data => data?.type === "earthquake" ? data : null}
			fallbackTitle={content => content.earthquake.place ?? "Earthquake"}
			accentBorder="border-red-400/20"
			pill="bg-red-400/90"
			headerExtras={content => <CutoutHeaderToggle earthquake={content.earthquake} />}
		>
			{(content, ctx) => <EarthquakeBody earthquake={content.earthquake} ctx={ctx} />}
		</AnchoredPanelShell>
	)
}

/**
 * The one action that opens the visually heavy view, so it is deliberately
 * not one more grey icon: a filled accent pill with a label, set apart from
 * the shared action cluster by a divider. Opening the cutout moves focus to
 * the block and this card gives way to the cutout HUD
 */
function CutoutHeaderToggle({ earthquake }: { earthquake: EarthquakeProperties }) {
	const dispatch = useAppDispatch()
	const open = useAppSelector(state => state.dialog.quakeCutout?.quakeId === earthquake.id)
	return (
		<div className="flex items-center shrink-0 mr-1 pr-2 border-r border-white/10">
			<PanelActionTooltip label={open ? "Hide the cross-section block" : "Lift a block of crust off the globe: hypocenters at depth, fault planes, history"}>
				<button
					onClick={() => dispatch(setQuakeCutout(open ? null : earthquake.id))}
					className={cn(
						"pointer-events-auto h-6 pl-2 pr-2.5 flex items-center gap-1.5 rounded-full shrink-0 whitespace-nowrap",
						"text-[11px] font-semibold tracking-wide text-white",
						"bg-red-500 shadow-md shadow-red-950/50 hover:bg-red-400 hover:scale-105 hover:shadow-lg transition-all",
						open && "bg-red-400 ring-2 ring-red-300/40"
					)}
					aria-pressed={open}
					aria-label={open ? "Hide cross-section" : "Show cross-section"}
				>
					<Cuboid className="w-3.5 h-3.5" />
					<span>Cross-section</span>
				</button>
			</PanelActionTooltip>
		</div>
	)
}

function EarthquakeBody({ earthquake, ctx }: { earthquake: EarthquakeProperties; ctx: PanelShellContext }) {
	const eventDate = new Date(earthquake.event_time)

	return (
		<>
			<div className="space-y-1">
				<div className="flex items-center gap-2">
					<span className="text-3xl font-bold text-white leading-none">
						M {earthquake.mag.toFixed(1)}
					</span>
					{earthquake.alert && (
						<Badge colorClass={getAlertColor(earthquake.alert)}>{earthquake.alert}</Badge>
					)}
					{earthquake.tsunami > 0 && (
						<Badge colorClass="bg-blue-500/20 text-blue-300 border border-blue-400/30">Tsunami</Badge>
					)}
				</div>
				<div className="text-sm text-slate-200 leading-snug">
					{ctx.title}
				</div>
			</div>

			<div className="flex items-center gap-2 text-xs text-slate-400">
				<span>{formatTimeAgo(eventDate)}</span>
				<span className="text-slate-600">&bull;</span>
				<span>{eventDate.toLocaleString()}</span>
			</div>

			<MoreSection compact={ctx.compact}>
				<div className="rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2.5 space-y-2">
					<div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
						<InfoField label="Depth" value={formatDepth(earthquake.depth_km)} />
						{earthquake.mag_type && (
							<InfoField label="Type" value={earthquake.mag_type.toUpperCase()} />
						)}
						{earthquake.felt != null && earthquake.felt > 0 && (
							<InfoField label="Felt" value={`${earthquake.felt.toLocaleString()} reports`} />
						)}
						{earthquake.mmi != null && (
							<InfoField label="MMI" value={earthquake.mmi.toFixed(1)} />
						)}
						{earthquake.cdi != null && (
							<InfoField label="CDI" value={earthquake.cdi.toFixed(1)} />
						)}
						{earthquake.sig != null && (
							<InfoField label="Significance" value={earthquake.sig.toString()} />
						)}
						<InfoField label="Status" value={earthquake.status} />
						<InfoField label="Network" value={earthquake.net.toUpperCase()} />
					</div>

					<CoordFooter
						lat={earthquake.lat}
						lon={earthquake.lon}
						extra={`${formatDepth(earthquake.depth_km)} deep`}
						className="pt-1 border-t border-white/5"
					/>
				</div>

				<CurrentConditionsSection
					lat={earthquake.lat}
					lon={earthquake.lon}
					accentClass="text-red-400"
				/>
			</MoreSection>
		</>
	)
}

function getAlertColor(alert: string | null): string {
	switch (alert) {
		case "red": return "bg-red-500/20 text-red-300 border border-red-400/30"
		case "orange": return "bg-orange-500/20 text-orange-300 border border-orange-400/30"
		case "yellow": return "bg-yellow-500/20 text-yellow-300 border border-yellow-400/30"
		case "green": return "bg-green-500/20 text-green-300 border border-green-400/30"
		default: return "bg-slate-500/20 text-slate-300 border border-slate-400/30"
	}
}
